<template>
  <div class="event-companies-editor">
    <el-table
      v-loading="fetchInProgress"
      :data="startups"
      empty-text="Please add some companies using button below"
    >
      <el-table-column type="expand">
        <template slot-scope="{ row }">
          <div
            v-for="user_rating in row.user_ratings"
            :key="user_rating.user_id"
            class="user-rating row"
          >
            <div class="col-sm-12 col-md-6 col-lg-4">
              <StartupRate
                class="inline-block"
                :value="user_rating.rating.overall.value"
                read-only
                muted
              />
              <span
                v-if="hasCategoryRatings(user_rating.rating)"
                class="show-more-ratings"
                >(<a
                  href
                  @click.prevent="
                    () => openRateDialog({ rating: user_rating.rating })
                  "
                  >more</a
                >)</span
              >
            </div>
            <div class="col-sm-12 col-md-6 col-lg-4">
              {{ user_rating.email }}
            </div>
            <div class="col-sm-12 col-md-12 col-lg-4">
              <p>{{ user_rating.rating.overall.note }}</p>
            </div>
          </div>

          <p
            v-if="
              !row.user_ratings ||
              (row.user_ratings && row.user_ratings.length === 0)
            "
          >
            Company has not been rated yet.
          </p>
        </template>
      </el-table-column>
      <el-table-column label="Company" prop="name" sortable>
        <template slot-scope="{ row }">
          <a :href="startupProfileLink(row)" target="_new">{{ row.name }}</a>
        </template>
      </el-table-column>
      <el-table-column
        label="Average Rating"
        prop="avg_ratings.overall"
        sortable
      >
        <template slot-scope="{ row }">
          <span v-if="row.avg_ratings">
            {{ row.avg_ratings.overall | number }}
          </span>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column label="Rating progress" width="160">
        <template slot-scope="{ row }">
          <div class="progress-cell">
            <el-tooltip
              :content="getProgressTooltipText({ startup: row })"
              placement="top"
            >
              <el-progress :percentage="getRatingProgress({ startup: row })" />
            </el-tooltip>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="Actions" width="120">
        <template slot-scope="{ row }">
          <el-tooltip content="Delete" placement="top">
            <a
              href
              class="btn btn-danger btn-xs"
              @click.prevent="removeStartup({ startup: row })"
            >
              <i class="ion ion-trash-b"></i>
            </a>
          </el-tooltip>
        </template>
      </el-table-column>
    </el-table>

    <br />

    <el-dropdown
      split-button
      type="primary"
      trigger="click"
      @click="$refs.addStartupToEventDialog.open()"
      @command="openAddCompanyDialog"
    >
      Add
      <el-dropdown-menu slot="dropdown">
        <el-dropdown-item command="application">
          Add with application only
        </el-dropdown-item>
      </el-dropdown-menu>
    </el-dropdown>

    <el-button @click.prevent="downloadAsExcel()">
      <i class="ion ion-android-download"></i>
      Download excel
    </el-button>

    <AddStartupToEventDialog
      ref="addStartupToEventDialog"
      :event-id="event.id"
      :organization-id="event.organization.id"
      @added="onStartupAdded"
    />
    <EventCompaniesAddByApplicationDialog
      ref="addCompaniesDialog"
      :event="event"
      :already-selected="startups"
      @add="onApplicationsAdded($event)"
    />

    <RateDialog
      ref="rateDialog"
      :startup-id="startupForRating.id"
      read-only
      @rated="$emit('rated', $event)"
    />
  </div>
</template>

<script>
import EventCompaniesAddByApplicationDialog from './EventCompaniesAddByApplicationDialog.vue'
import StartupRate from 'components/StartupRate.vue'
import AddStartupToEventDialog from 'components/organizations/AddStartupToEventDialog.vue'
import { mapState } from 'vuex'
import { hasCategoryRatings } from 'services/rating-categories'
import RateDialog from 'components/startups/RateDialog.vue'
import { getNewAppUrl } from '@/utils/urls'

export default {
  components: {
    AddStartupToEventDialog,
    EventCompaniesAddByApplicationDialog,
    RateDialog,
    StartupRate,
  },
  props: {
    event: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    ...mapState({
      users: (s) => s.eventDetails.users,
      usersFetchInProgress: (s) => s.eventDetails.usersFetchInProgress,
      fetchInProgress: (s) =>
        s.startupList.fetchInProgress || s.eventDetails.startupsFetchInProgress,
      startups: (s) => {
        if (s.startupList.startups.length) {
          return s.startupList.startups
        } else {
          return s.eventDetails.startups
        }
      },
    }),
  },
  data() {
    return {
      startupForRating: {},
    }
  },
  methods: {
    async onApplicationsAdded(applications) {
      console.log(' => onApplicationsAdded:', applications)
      const startups = applications.map((a) => a.startup_id)
      await this.$store.dispatch('eventDetails/addStartups', { startups })
      this.fetch()
    },

    async onStartupAdded({ startup }) {
      const startups = [startup.id]
      await this.$store.dispatch('eventDetails/addStartups', { startups })
      this.fetch()
    },

    fetch() {
      // this.$store.dispatch('startupList/fetch', { event_id: this.event.id })
      this.$store.dispatch('eventDetails/fetchStartups', {
        eventId: this.event.id,
      })
    },

    async removeStartup({ startup }) {
      await this.$store.dispatch('eventDetails/removeStartup', {
        startupId: startup.id,
      })
      this.$notify({
        type: 'success',
        title: 'Success',
        message: 'Company has been removed',
      })
    },

    getRatingProgress({ startup }) {
      const usersCount = this.users.length
      if (usersCount === 0) {
        return 0
      } else {
        const userRatings = this.getUserRatings({ startup })
        const progress = (userRatings.length * 100) / usersCount
        return new Number(progress.toFixed(1))
      }
    },

    getProgressTooltipText({ startup }) {
      const usersCount = this.users.length
      if (usersCount === 0) {
        return null
      } else {
        const userRatings = this.getUserRatings({ startup })
        const votes = userRatings.length
        return `Votes: ${votes} out of ${usersCount}`
      }
    },

    getUserRatings({ startup }) {
      return startup.user_ratings || []
    },

    startupProfileLink(startup) {
      const url = this.$router.resolve({
        name: 'publicStartupProfile',
        params: { slug: startup.slug || startup.id },
        query: { showroomId: this.event.id },
      }).href
      return getNewAppUrl(url)
    },

    openAddCompanyDialog(command) {
      if (command === 'application') {
        this.$refs.addCompaniesDialog.open()
      }
    },

    openRateDialog({ rating }) {
      this.$refs.rateDialog.open(rating)
    },

    hasCategoryRatings,

    async downloadAsExcel() {
      const eventId = this.event.id
      http.downloadFile(
        `/events/${eventId}/startup-ratings.xlsx`,
        'showroom-ratings.xlsx',
      )
    },
  },
}
</script>

<style lang="scss">
.event-companies-editor {
  .el-table {
    .progress-cell {
      .el-progress__text {
        font-size: 10px !important;
      }
    }
  }
}

.show-more-ratings {
  font-size: 10px;
}
</style>
